/**
 * Servizio per mappare i template email alle proprietà delle notifiche in-app.
 *
 * Type e action sono dedotti dal template (non salvati nel DB); title e message
 * sono chiavi i18n risolte al momento del render, così cambiando lingua le
 * notifiche già in elenco si ritraducono.
 *
 * Convenzione delle chiavi: per ogni `template_key` di `email_templates` con
 * `notification_recipients` non vuoto esistono
 *   notifications.templates.<template_key>.title
 *   notifications.templates.<template_key>.message
 * in ENTRAMBI i file di traduzione. Aggiungendo un template a DB va aggiunta
 * qui la config e le due chiavi, altrimenti si ricade sul fallback `default`.
 */

import { NotificationAction, NotificationType } from '@/types/notification.types';
import type { TranslationKey } from '@/types/i18n.types';

export type Translate = (key: TranslationKey, options?: Record<string, unknown>) => string;

export interface NotificationMapping {
  type: NotificationType;
  title: string;
  message: string;
  action?: NotificationAction;
}

interface TemplateConfig {
  type: NotificationType;
  action?: (payload: any) => NotificationAction | undefined;
}

const TEMPLATE_FALLBACK = 'default';

// --- action builders condivisi -------------------------------------------------

const toRequest = (payload: any): NotificationAction | undefined =>
  payload?.travel_request_id
    ? {
        type: 'redirect',
        payload: { path: '/dashboard', params: { tab: 'requests', requestId: payload.travel_request_id } },
      }
    : undefined;

const toItineraryBuilder = (payload: any): NotificationAction | undefined =>
  payload?.itinerary_id
    ? {
        type: 'redirect',
        payload: { path: '/itinerary-builder', params: { itineraryId: payload.itinerary_id } },
      }
    : undefined;

const toItinerary = (payload: any): NotificationAction | undefined =>
  payload?.itinerary_id
    ? { type: 'redirect', payload: { path: '/itinerary-builder', params: { itineraryId: payload.itinerary_id } } }
    : undefined;

const toPayment = (payload: any): NotificationAction | undefined =>
  payload?.payment_id
    ? { type: 'redirect', payload: { path: '/payments', params: { paymentId: payload.payment_id } } }
    : undefined;

const toLegalSettings = (): NotificationAction => ({
  type: 'redirect',
  payload: { path: '/settings', params: { tab: 'legal' } },
});

const toUser = (idField: string) => (payload: any): NotificationAction | undefined =>
  payload?.[idField]
    ? { type: 'redirect', payload: { path: '/users', params: { userId: payload[idField] } } }
    : undefined;

/**
 * Config per template. Allineata a `email_templates.template_key`
 * (solo quelli con notification_recipients non vuoto).
 */
const TEMPLATE_CONFIG: Record<string, TemplateConfig> = {
  // ========== ITINERARY STATUS CHANGES ==========
  update_itinerary_from_draft_to_pending: { type: 'info', action: toItineraryBuilder },
  update_itinerary_from_pending_to_approved: { type: 'success', action: toItineraryBuilder },
  update_itinerary_from_pending_to_rejected: { type: 'error', action: toItineraryBuilder },
  update_itinerarys_from_sended_to_completed: { type: 'success', action: toItineraryBuilder },
  itinerary_request_changes: { type: 'warning', action: toItineraryBuilder },
  send_quote: { type: 'success', action: toItineraryBuilder },

  // Incasso dichiarato fuori Stripe: la fee non e' stata trattenuta e resta a debito.
  // 'warning' e non 'success': l'incasso e' una buona notizia, ma cio' che questa
  // notifica esiste per dire e' la posizione aperta che si porta dietro.
  manual_payment_platform_fee_due: { type: 'warning', action: toPayment },

  // L'agenzia ha aggiornato i testi legali e chiede ai membri di rivedere i propri.
  // Notifica solo in-app (request_legal_update_from_members), senza email_template.
  legal_settings_update_requested: { type: 'warning', action: toLegalSettings },

  // ========== TRAVEL REQUEST ==========
  receved_new_travel_request: { type: 'info', action: toRequest },
  received_new_travel_request_agency: { type: 'info', action: toRequest },
  received_new_travel_request_travel_designer: { type: 'info', action: toRequest },
  assign_request: { type: 'info', action: toRequest },
  travel_request_accepted_by_designer: { type: 'success', action: toRequest },
  travel_request_rejected_by_designer: { type: 'warning', action: toRequest },

  // ========== ACCOUNT / ONBOARDING ==========
  travel_designer_request: { type: 'info', action: toUser('td_id') },
  approve_new_travel_designer: { type: 'success' },
  reject_new_travel_designer: { type: 'error' },
  approve_new_travel_designer_member: { type: 'info' },
  agency_request: { type: 'info', action: toUser('agency_id') },
  approve_new_agency: { type: 'success' },
  reject_new_agency: { type: 'warning' },

  // ========== PAYMENTS ==========
  notify_payment_received: { type: 'success', action: toPayment },
  payment_failed: { type: 'error', action: toPayment },
  payment_refunded: { type: 'warning', action: toPayment },
  payment_disputed: { type: 'warning', action: toPayment },

  // ========== INSTALLMENTS ==========
  installment_due: { type: 'info', action: toItinerary },
  installment_overdue: { type: 'warning', action: toItinerary },

  // ========== FALLBACK ==========
  [TEMPLATE_FALLBACK]: { type: 'info' },
};

export class NotificationTemplateMapper {
  /**
   * Normalizza il payload del template nelle variabili usate dalle traduzioni.
   * I payload sono piatti, ma le versioni storiche annidavano le variabili
   * sotto `variables`: entrambe le forme restano supportate.
   */
  private static buildValues(payload: any, t: Translate): Record<string, string> {
    const v = payload?.variables ?? payload ?? {};

    const fullName = (first?: string, last?: string) =>
      [first, last].filter(Boolean).join(' ').trim();

    const destinations = Array.isArray(v.destinations)
      ? v.destinations.filter(Boolean).join(', ')
      : v.destinations || '';

    return {
      clientName: fullName(v.client_first_name, v.client_last_name) || t('notifications.fallback.client'),
      tdName: fullName(v.td_first_name, v.td_last_name) || t('notifications.fallback.designer'),
      agencyName: fullName(v.agency_first_name, v.agency_last_name) || t('notifications.fallback.agency'),
      payerName: fullName(v.payer_first_name, v.payer_last_name) || t('notifications.fallback.client'),
      itineraryTitle: v.itinerary_title || t('notifications.fallback.itinerary'),
      installmentLabel: v.installment_label || '',
      amount: v.amount != null ? String(v.amount) : '',
      currency: v.currency || '',
      daysOverdue: v.days_overdue != null ? String(v.days_overdue) : '',
      dueDate: v.due_date || '',
      destinations,
      recordedByName: v.recorded_by_name || t('notifications.fallback.user'),
      platformFeeAmount: v.platform_fee_amount != null ? String(v.platform_fee_amount) : '',
    };
  }

  /**
   * Risolve un template in type, title, message e action già pronti per la UI.
   * `t` va passato dal chiamante (React) perché il risultato dipende dalla lingua attiva.
   */
  static mapTemplate(template: string | null, payload: any = {}, t: Translate): NotificationMapping {
    const key = template && TEMPLATE_CONFIG[template] ? template : TEMPLATE_FALLBACK;

    if (template && key === TEMPLATE_FALLBACK) {
      console.warn(`[NotificationTemplateMapper] Template sconosciuto: ${template}, uso default`);
    }

    const config = TEMPLATE_CONFIG[key];
    const values = this.buildValues(payload, t);

    return {
      type: config.type,
      title: t(`notifications.templates.${key}.title` as TranslationKey),
      message: t(`notifications.templates.${key}.message` as TranslationKey, values),
      action: config.action?.(payload),
    };
  }

  /** True se il template ha una traduzione dedicata (ossia non ricade sul default). */
  static isKnownTemplate(template: string | null): boolean {
    return !!template && template !== TEMPLATE_FALLBACK && !!TEMPLATE_CONFIG[template];
  }
}
